/*
 * Client lifecycle (SPEC decision 26, §17.6): the pure decisions behind the Clients list and the client page — how a
 * status reads, which actions a client offers in each status, the details and invite validation, the slug typed to
 * delete, and how a deletion's steps read. The API stays the authority: these only shape what is offered.
 */
import type { ClientDeletionStep, ClientDeletionStepKey, ClientOrgStatus, ManualRemoval, PlatformOrgUpdateRequest } from '@hangar/shared';
import type { Invitation } from '@/api/schemasMembers';
import type { Tone } from '@/components/badges';

export interface StatusView {
  label: string;
  /** A status color token, always shown with a dot or spinner and the label (never color alone). */
  tone: Tone;
  /** Spinner while Hangar works on it. */
  busy?: boolean;
}

const STATUS_VIEW: Record<ClientOrgStatus, StatusView> = {
  active: { label: 'Active', tone: 'succeeded' },
  suspended: { label: 'Suspended', tone: 'awaiting' },
  deleting: { label: 'Deleting', tone: 'running', busy: true },
  deletion_failed: { label: 'Deletion failed', tone: 'failed' },
};

/** The badge of a client's status; a status this console does not know yet reads as itself. */
export const statusView = (status: string): StatusView => STATUS_VIEW[status as ClientOrgStatus] ?? { label: status.replace(/_/g, ' '), tone: 'subtle' };

/* ---------- What a client offers ---------- */

export type ClientActionKey = 'edit' | 'invite' | 'suspend' | 'reactivate' | 'delete' | 'deletion' | 'preview';

/**
 * The actions of a client in each status. Delete only once suspended; a client being deleted (or whose deletion
 * failed) only leads to its deletion, which resumes from the failed step. Reactivate is refused while a deletion
 * exists (409), so it is not offered then.
 */
export function clientActions(status: string): ClientActionKey[] {
  switch (status) {
    case 'active':
      return ['edit', 'invite', 'suspend', 'preview'];
    case 'suspended':
      return ['edit', 'invite', 'reactivate', 'delete', 'preview'];
    case 'deleting':
    case 'deletion_failed':
      return ['deletion'];
    default:
      return ['preview'];
  }
}

/** The sections of the client page (`/clients/$orgId?section=`). */
export const CLIENT_SECTIONS = ['details', 'people', 'status', 'danger'] as const;
export type ClientSection = (typeof CLIENT_SECTIONS)[number];
export const clientSectionAnchor = (s: ClientSection): string => `client-${s}`;

/** Where a row action that is not a dialog opens the client page. */
export function sectionOfAction(key: ClientActionKey): ClientSection | undefined {
  if (key === 'edit') return 'details';
  if (key === 'invite') return 'people';
  return key === 'delete' || key === 'deletion' ? 'danger' : undefined;
}

export const deletionActionLabel = (status: string): string => (status === 'deletion_failed' ? 'Retry deletion' : 'View deletion');

/* ---------- Details: name and email domains ---------- */

const DOMAIN = /^(?=.{3,253}$)[a-z0-9-]+(\.[a-z0-9-]+)+$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MAX_DOMAINS = 10;

export type FieldErrors<K extends string> = Partial<Record<K, string>>;

/** `acme.com, @acme.io  ACME.com` → `['acme.com', 'acme.io']`: commas, spaces or lines; lowercase; no repeats. */
export function parseDomains(text: string): string[] {
  const out = text.split(/[\s,;]+/).map((d) => d.trim().toLowerCase().replace(/^@/, '')).filter(Boolean);
  return [...new Set(out)];
}

export interface DetailsValues {
  name: string;
  domains: string;
}

export function validateDetails(v: DetailsValues): FieldErrors<'name' | 'emailDomains'> {
  const errors: FieldErrors<'name' | 'emailDomains'> = {};
  if (!v.name.trim()) errors.name = "Enter the client's name";
  else if (v.name.trim().length > 120) errors.name = 'At most 120 characters';
  const domains = parseDomains(v.domains);
  const bad = domains.find((d) => !DOMAIN.test(d));
  if (domains.length === 0) errors.emailDomains = 'Enter at least one email domain, like acme.com';
  else if (bad) errors.emailDomains = `“${bad}” is not a domain such as acme.com`;
  else if (domains.length > MAX_DOMAINS) errors.emailDomains = `At most ${MAX_DOMAINS} domains`;
  return errors;
}

/** The merge patch of what changed, or `null` when nothing did. */
export function detailsPatch(org: { name: string; emailDomains: string[] }, v: DetailsValues): PlatformOrgUpdateRequest | null {
  const patch: PlatformOrgUpdateRequest = {};
  if (v.name.trim() !== org.name) patch.name = v.name.trim();
  const domains = parseDomains(v.domains);
  if (domains.join(',') !== org.emailDomains.join(',')) patch.emailDomains = domains;
  return Object.keys(patch).length ? patch : null;
}

/** Maps an API field error of the details form to its field. */
export const detailsFieldOf = (field: string): 'name' | 'emailDomains' | undefined => (field === 'name' || field === 'emailDomains' ? field : undefined);

/* ---------- Invite people ---------- */

export type ClientInviteRole = 'admin' | 'member';

export const CLIENT_ROLE_OPTIONS: readonly { value: ClientInviteRole; label: string; description: string }[] = [
  { value: 'admin', label: 'Admin', description: 'Everything in the client, including its members and integrations' },
  { value: 'member', label: 'Member', description: 'Builds and environment variables; no member management' },
];

export interface ClientInviteValues {
  email: string;
  name: string;
  role: ClientInviteRole;
}

export const emailDomain = (email: string): string => email.trim().toLowerCase().split('@')[1] ?? '';

/** Inline validation: an operator invites only inside the client's domains (guests stay the client admins' call). */
export function validateClientInvite(v: ClientInviteValues, domains: readonly string[], pending: readonly Invitation[] = []): FieldErrors<'email' | 'name'> {
  const email = v.email.trim().toLowerCase();
  const errors: FieldErrors<'email' | 'name'> = {};
  if (!email) errors.email = 'Enter the email address to invite';
  else if (!EMAIL.test(email)) errors.email = 'Enter a valid email address, like name@company.com';
  else if (domains.length && !domains.includes(emailDomain(email))) errors.email = `Use an address at ${domains.join(' or ')}: people outside the client's domains are invited by its admins`;
  else if (pending.some((i) => i.email.toLowerCase() === email)) errors.email = 'An invitation for this email is pending: resend it instead';
  if (v.name.trim().length > 120) errors.name = 'At most 120 characters';
  return errors;
}

/* ---------- Suspend and delete ---------- */

/** What suspending does, for the confirm dialog (SPEC §17.6). */
export const SUSPEND_EFFECTS: readonly string[] = [
  'Nobody in the client can use Hangar from their next request: user sessions, API tokens and CI tokens are refused with “suspended”.',
  'Its queued and running builds are canceled; reactivating does not restart them.',
  'Nothing is deleted. You can still list and preview it, and reactivate it at any time.',
];

export const SUSPEND_REASON_MAX = 500;

export function validateSuspendReason(reason: string): string | undefined {
  if (!reason.trim()) return 'Say why: operators read it on the client page (the client never sees it)';
  return reason.trim().length > SUSPEND_REASON_MAX ? `At most ${SUSPEND_REASON_MAX} characters` : undefined;
}

/** The slug typed to delete matches exactly (no case folding: the slug is what the API compares). */
export const slugConfirmed = (typed: string, slug: string): boolean => typed.trim() === slug;

interface DeletableClient {
  memberCount: number;
  openInvitations: number;
  providers: { eas: string | null; bitrise: string | null };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Exactly what a deletion removes, one line each (operators only: provider names are fine here). */
export function deletionRemovals(o: DeletableClient): string[] {
  const lines = [
    `${plural(o.memberCount, 'membership', 'memberships')} and ${plural(o.openInvitations, 'open invitation', 'open invitations')} (people keep their Hangar user: they may belong elsewhere)`,
    'Every project, app, environment, build profile, build and env variable record of the client in Hangar’s database',
    'Its environment variable values in Parameter Store, and their S3 overflow',
    'Its env snapshots and build artifacts in S3',
  ];
  if (o.providers.bitrise) lines.push(`Bitrise apps Hangar created for it; apps it reused only lose Hangar’s webhook and signing files (Malible’s workspace ${o.providers.bitrise} stays)`);
  if (o.providers.eas) lines.push(`On Expo: Hangar’s webhooks and the robot’s access tokens. The Expo organization ${o.providers.eas}, its projects and the robot need sudo mode: you remove those by hand (listed after the deletion)`);
  return lines;
}

/* ---------- Deletion progress ---------- */

const STEP_LABEL: Record<ClientDeletionStepKey, string> = {
  providers: 'Build providers (Expo, Bitrise)',
  env_values: 'Environment variable values',
  storage: 'Env snapshots and artifacts',
  database: 'Database rows and the organization',
};
export const stepLabel = (key: string): string => STEP_LABEL[key as ClientDeletionStepKey] ?? key.replace(/_/g, ' ');

export type DeletionState = 'done' | 'failed' | 'running';

export interface DeletionSummary {
  state: DeletionState;
  done: number;
  total: number;
  failed: ClientDeletionStep | undefined;
  /** What the operator removes by hand, across every step. */
  manual: ManualRemoval[];
  headline: string;
}

export function deletionSummary(steps: readonly ClientDeletionStep[]): DeletionSummary {
  const done = steps.filter((s) => s.status === 'done').length;
  const failed = steps.find((s) => s.status === 'failed');
  const manual = steps.flatMap((s) => s.manual ?? []);
  const state: DeletionState = failed ? 'failed' : steps.length > 0 && done === steps.length ? 'done' : 'running';
  const tail = manual.length ? `; ${plural(manual.length, 'item', 'items')} to remove by hand` : '';
  const headline =
    state === 'failed' ? `Stopped at “${stepLabel(failed!.key)}”: retry to resume from it${tail}` : state === 'done' ? `Deleted: ${done} of ${steps.length} steps done${tail}` : `Deleting: ${done} of ${steps.length} steps done`;
  return { state, done, total: steps.length, failed, manual, headline };
}

export interface StepView {
  tone: Tone;
  icon: 'check_circle' | 'error' | 'schedule';
  text: string;
}

export function deletionStepView(step: ClientDeletionStep): StepView {
  if (step.status === 'done') return { tone: 'succeeded', icon: 'check_circle', text: step.manual?.length ? `Done; ${plural(step.manual.length, 'item', 'items')} to remove by hand` : 'Done' };
  if (step.status === 'failed') return { tone: 'failed', icon: 'error', text: `Failed${step.message ? `: ${step.message}` : ''}` };
  return { tone: 'queued', icon: 'schedule', text: 'Pending' };
}

/** A manual removal's `where` as a link only when it is an https URL (anything else is shown as text). */
export function safeLink(where: string): string | undefined {
  try {
    const url = new URL(where);
    return url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}
